import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchData, postData } from '../../Utils/Server';
import Endpoints from '../../Constants/Endpoints';
import { ReasonCaptureModal } from '../Common/ReasonCaptureModal';
import { Guard } from '../Common/Guard';
import { PERMS } from '../../Utils/Permissions';
import PageLayout from '../Layout/PageLayout';
import { Card, Button, StatusBadge, Spinner, tokens } from '../UI';

const fmtKES = (cents) => `KES ${((cents || 0) / 100).toFixed(2)}`;

const Row = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: tokens.spacing[4],
      padding: `${tokens.spacing[2]} 0`,
      borderBottom: `1px solid ${tokens.colors.divider}`,
    }}
  >
    <span style={{ color: tokens.colors.muted, fontWeight: tokens.typography.weight.semibold }}>
      {label}
    </span>
    <span style={{ color: tokens.colors.body, textAlign: 'right' }}>{value}</span>
  </div>
);

export default function LoanDetails() {
  const { id } = useParams();
  const [loan, setLoan] = useState(null);
  const [pending, setPending] = useState(null); // 'approve' | 'reject' | 'disburse' | null

  const load = useCallback(() => {
    // No dedicated GET-by-id endpoint exists; fetch from pending list and
    // match by id. For non-pending statuses, the page falls back to the
    // last-known state from props/state.
    fetchData(Endpoints.LIST_PENDING_LOANS)
      .then((r) => r.json())
      .then((res) => {
        const list = (res && res.data) || res || [];
        const found = list.find((l) => l.id === id);
        if (found) setLoan(found);
      });
  }, [id]);

  useEffect(load, [load]);

  const submit = (reason) => {
    const fd = new FormData();
    fd.append('reason', reason);
    let endpoint;
    if (pending === 'approve') endpoint = Endpoints.APPROVE_LOAN(id);
    else if (pending === 'reject') endpoint = Endpoints.REJECT_LOAN(id);
    else if (pending === 'disburse') endpoint = Endpoints.DISBURSE_LOAN(id);
    else return;
    postData(endpoint, fd)
      .then((r) => r.json())
      .then((res) => {
        setLoan((res && res.data) || res);
        setPending(null);
      });
  };

  if (!loan) return <PageLayout title="Fuel loan"><Spinner center label="Loading..." /></PageLayout>;

  return (
    <PageLayout title="Fuel loan">
      <Card style={{ maxWidth: 620 }}>
        <Row label="Rider" value={<code>{loan.rider_id}</code>} />
        <Row label="Status" value={<StatusBadge status={loan.status}>{loan.status}</StatusBadge>} />
        <Row label="Principal" value={fmtKES(loan.principal_cents)} />
        <Row label="Fee" value={fmtKES(loan.fee_cents)} />
        <Row label="Repaid" value={fmtKES(loan.repaid_cents)} />
        {loan.approved_by_id && <Row label="Approved by" value={<code>{loan.approved_by_id}</code>} />}
        {loan.rejection_reason && <Row label="Rejection reason" value={loan.rejection_reason} />}
        {loan.approved_at && <Row label="Approved at" value={new Date(loan.approved_at).toLocaleString()} />}
        {loan.disbursed_at && <Row label="Disbursed at" value={new Date(loan.disbursed_at).toLocaleString()} />}
        {loan.settled_at && <Row label="Settled at" value={new Date(loan.settled_at).toLocaleString()} />}

        <Guard permission={PERMS.FINANCIAL_WRITE}>
          <div style={{ marginTop: tokens.spacing[5], display: 'flex', gap: tokens.spacing[2] }}>
            {loan.status === 'pending' && (
              <>
                <Button onClick={() => setPending('approve')}>Approve</Button>
                <Button variant="danger" onClick={() => setPending('reject')}>Reject</Button>
              </>
            )}
            {loan.status === 'approved' && (
              <Button onClick={() => setPending('disburse')}>Disburse now</Button>
            )}
          </div>
        </Guard>
      </Card>

      {pending && (
        <ReasonCaptureModal
          title={
            pending === 'approve' ? 'Approve loan' :
            pending === 'reject'  ? 'Reject loan' :
                                    'Disburse loan'
          }
          onCancel={() => setPending(null)}
          onConfirm={submit}
        />
      )}
    </PageLayout>
  );
}
